import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Money } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDate } from '@/lib/format'
import type { AdminReviewDto } from '@/lib/types'

export function AccountDetail() {
  const { t, i18n } = useTranslation('admin')
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const [actionError, setActionError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const q = useQuery({
    queryKey: ['admin-account', id],
    queryFn: () => adminApi.getAccount(id),
    enabled: Boolean(id),
  })
  const account = q.data?.account

  async function act(fn: () => Promise<unknown>) {
    setActionError(null)
    setBusy(true)
    try {
      await fn()
      await qc.invalidateQueries({ queryKey: ['admin-account', id] })
    } catch (e) {
      setActionError(apiErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  function ReviewCard({ review }: { review: AdminReviewDto }) {
    return (
      <div className="border-b border-border py-3 last:border-0">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">
            {t(`review_types.${review.type}`, { defaultValue: review.type })}
          </span>
          <span className="text-xs text-text-muted">
            {t(`review_statuses.${review.status}`, { defaultValue: review.status })}
          </span>
        </div>
        {review.reason_public ? (
          <p className="text-sm text-text-muted">{review.reason_public}</p>
        ) : null}
        {review.reason_internal ? (
          <p className="text-xs text-warning">{review.reason_internal}</p>
        ) : null}
        <p className="mt-1 text-xs text-text-subtle">
          {t('reviews.started')} {formatDate(review.started_at, i18n.language)}
        </p>
        {review.status === 'in_progress' ? (
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={() => act(() => adminApi.resolveReview(review.id))}
            >
              {t('reviews.resolve')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => act(() => adminApi.assignReview(review.id))}
            >
              {t('reviews.assign_me')}
            </Button>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <Link to="/accounts" className="text-sm text-text-muted hover:text-text">
        {t('common.back')}
      </Link>

      {q.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}
      {actionError ? <ErrorBanner message={actionError} /> : null}

      {account ? (
        <>
          <div className="rounded-lg border border-border p-4">
            <h1 className="text-xl font-semibold">{account.owner.email}</h1>
            <dl className="mt-3 grid grid-cols-3 gap-y-2 text-sm">
              <dt className="text-text-muted">{t('accounts.col_iban')}</dt>
              <dd className="col-span-2 break-all font-mono">{account.iban}</dd>
              <dt className="text-text-muted">{t('accounts.col_status')}</dt>
              <dd className="col-span-2">{t(`account_statuses.${account.status}`)}</dd>
              <dt className="text-text-muted">{t('accounts.col_balance')}</dt>
              <dd className="col-span-2">
                <Money
                  amountCents={account.available_balance_cents}
                  currency={account.currency}
                  locale={i18n.language}
                />
              </dd>
            </dl>
          </div>

          <div className="rounded-lg border border-border p-4">
            <h2 className="mb-2 text-lg font-medium">{t('reviews.title')}</h2>
            {q.data && q.data.reviews.length === 0 ? (
              <p className="text-sm text-text-muted">{t('reviews.empty')}</p>
            ) : null}
            {q.data?.reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
          </div>
        </>
      ) : null}
    </div>
  )
}
